import { CanvasTexture, Mesh, MeshBasicMaterial, SRGBColorSpace } from 'three';

import WebGLObject from './WebGLObject';
import { PLANE_GEOMETRY } from '../../../3D/constants';
import { Sizes } from '../core/Sizes';

export default class Text3D extends WebGLObject {
    text;
    color;
    bg;
    fontSize;
    fontFamily;
    textAlign;

    geometry;
    material;
    texture;

    constructor(opts = {}) {
        super(opts);

        this.text = opts.text || 'Text';

        this.init();
    }

    init() {
        this.computeStyle();
        this.drawTextCanvas();

        this.geometry = PLANE_GEOMETRY;
        this.material = new MeshBasicMaterial({ map: this.texture, transparent: true });
        this.mesh = new Mesh(this.geometry, this.material);
        this.add(this.mesh);

        this.resize();
    }

    computeStyle() {
        const computedStyle = getComputedStyle(this.dom);
        this.color = computedStyle.color;
        this.bg = computedStyle.backgroundColor;
        this.fontSize = parseInt(computedStyle.fontSize);
        this.fontFamily = computedStyle.fontFamily;
        this.fontWeight = computedStyle.fontWeight;
        this.lineHeight = parseFloat(computedStyle.lineHeight);
        this.textAlign = computedStyle.textAlign;
        this.font = `${this.fontWeight} ${this.fontSize}px ${this.fontFamily}`;
    }

    wrapText(ctx, text, maxWidth) {
        const words = text.split(' ');
        const lines = [];
        let currentLine = words[0];

        for (let i = 1; i < words.length; i++) {
            const word = words[i];
            const width = ctx.measureText(currentLine + ' ' + word).width;
            if (width < maxWidth) {
                currentLine += ' ' + word;
            } else {
                lines.push(currentLine);
                currentLine = word;
            }
        }
        lines.push(currentLine);
        return lines;
    }

    drawTextCanvas() {
        if (!this.canvas) {
            this.canvas = document.createElement('canvas');
            this.ctx = this.canvas.getContext('2d');
        }

        const { width, height } = this.dom.getBoundingClientRect();
        this.canvas.width = width * Sizes.RATIO;
        this.canvas.height = height * Sizes.RATIO;
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.scale(Sizes.RATIO, Sizes.RATIO);

        if (this.bg) {
            this.ctx.fillStyle = this.bg;
            this.ctx.fillRect(0, 0, width, height);
        }

        this.ctx.fillStyle = this.color;
        this.ctx.font = this.font;
        this.ctx.textAlign = this.textAlign;
        this.ctx.textBaseline = 'middle';

        const maxWidth = width;
        const lines = this.wrapText(this.ctx, this.text, maxWidth);
        const totalTextHeight = lines.length * this.lineHeight;
        let startY = (height - totalTextHeight) / 2 + this.lineHeight / 2;

        const x = this.textAlign === 'center' ? width / 2 : 0;
        for (let i = 0; i < lines.length; i++) {
            const y = startY + i * this.lineHeight;
            this.ctx.fillText(lines[i], x, y);
        }

        if (!this.texture) {
            this.texture = new CanvasTexture(this.canvas);
        } else {
            this.texture.image = this.canvas;
        }

        this.texture.colorSpace = SRGBColorSpace;
        this.texture.needsUpdate = true;
    }

    update() {
        super.update();

        const { x, y, width, height } = this.dom.getBoundingClientRect();
        const position = this.domPositionTo3D(x, y);
        this.pos.x = position.x + width * .5;
        this.pos.y = position.y - height * .5;
        this.pos.z = parseInt(this.dom.dataset.layer);
    }

    resize() {
        const { x, y, width, height } = this.dom.getBoundingClientRect();
        const position = this.domPositionTo3D(x, y);

        this.position.x = this.pos.x = position.x + width * .5;
        this.position.y = this.pos.y = position.y - height * .5;
        this.pos.z = parseInt(this.dom.dataset.layer);

        super.resize(width, height);
    }
}
